import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useDataFetcher } from 'hooks/useDataFetcher/useDataFetcher';
import GSApi from 'api/Api';
import { Fetcher, FetchParamById, FetchResponse } from 'types/ApiTypes';
import { decodeId } from 'utils/fnUrl';
import { useGlobalNetworkError, useGlobalNoInternet } from 'hooks/withNetworkCheck/withNetworkCheck';
import { usePersistentState } from 'hooks/useStorage/useStorage';
import { PlaybackPositionsResponse } from 'types/Player';
import { useAuth } from 'providers/useAuth/AuthContext';

type VodParams = {
    vodId: string;
    position: number;
};

type ListingParams = {
    listingId: string;
    position: number;
};

const usePlaybackService = () => {
    const { onNoInternet } = useGlobalNoInternet();
    const { onNetworkError } = useGlobalNetworkError();
    const { isGuest, isLoggedIn } = useAuth();
    const [hasInitialFetch, setHasInitialFetch] = useState(false);
    const [playbackAssetReloaded, setPlaybackAssetReloaded] = useState(false);

    const {
        response: syncResponse,
        loading,
        fetcher: fetchPositions,
    } = useDataFetcher<PlaybackPositionsResponse, void>(() => GSApi.getPlaybackPositions(), onNoInternet, onNetworkError);

    const { response: saveResponse, fetcher: postPlaybackPosition } = useDataFetcher<PlaybackPositionsResponse, VodParams | ListingParams>(
        (params) =>
            'vodId' in params
                ? GSApi.createVODPlaybackPosition(params.vodId, params.position)
                : GSApi.createListingPlaybackPosition(params.listingId, params.position),
        onNoInternet,
        onNetworkError
    );

    const { set: setPositions, store: positions } = usePersistentState('playbackPositions', {
        listing_playback_positions: {},
        vod_asset_playback_positions: {},
    });

    useEffect(() => {
        if (!isLoggedIn) {
            setHasInitialFetch(false);
        }
    }, [isLoggedIn]);

    useEffect(() => {
        if (isLoggedIn && !hasInitialFetch && !isGuest) {
            fetchPositions();
            setHasInitialFetch(true);
        }
    }, [isLoggedIn, isGuest, hasInitialFetch]);

    // Sync data with API fetch
    useEffect(() => {
        if (syncResponse) {
            const currentPositions = positions;
            const updatedPositions = {
                listing_playback_positions: {
                    ...currentPositions.listing_playback_positions,
                    ...syncResponse.listing_playback_positions,
                },
                vod_asset_playback_positions: {
                    ...currentPositions.vod_asset_playback_positions,
                    ...syncResponse.vod_asset_playback_positions,
                },
            };

            setPositions(updatedPositions);
        }
    }, [syncResponse]);

    useEffect(() => {
        setPlaybackAssetReloaded(false);
    }, [saveResponse]);

    const savePlaybackPosition = useCallback(async (id: string, position: number, isVod: boolean): Promise<boolean> => {
        const roundedPosition = Math.floor(position);

        // Update local state
        setPositions((prev) => ({
            ...prev,
            [isVod ? 'vod_asset_playback_positions' : 'listing_playback_positions']: {
                ...prev[isVod ? 'vod_asset_playback_positions' : 'listing_playback_positions'],
                [id]: roundedPosition,
            },
        }));

        const result = await postPlaybackPosition(
            isVod ? { vodId: id, position: roundedPosition } : { listingId: id, position: roundedPosition }
        );

        return !!result && !(result as FetchResponse<PlaybackPositionsResponse>).error;
    }, []);

    return {
        savePlaybackPosition,
        saveResponse,
        loading,
        playbackAssetReloaded,
        setPlaybackAssetReloaded,
        setPositions,
        positions,
    };
};

export const PlaybackContext = createContext<ReturnType<typeof usePlaybackService>>(null);

export const PlaybackProvider = ({ children }) => (
    <PlaybackContext.Provider value={usePlaybackService()}>{children}</PlaybackContext.Provider>
);

export const usePlayback = () => useContext(PlaybackContext);

export const useDetailsReloadOnPlaybackUpdate = (fetcher: Fetcher<FetchParamById>, id: string) => {
    const { playbackAssetReloaded } = usePlayback();

    useEffect(() => {
        if (playbackAssetReloaded) {
            fetcher({ id: decodeId(id) }, true);
        }
    }, [playbackAssetReloaded]);
};
